import React from "react";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

// constants
import constants from "../../constants";

// mixpanel
import {
  trackFooterNavClick,
  trackSocialClick,
  trackAppStoreClick,
  trackContactClick,
  trackLegalLinkClick,
  trackFooterLogoClick,
} from "./footerMixpanel";

// styles
import * as styles from "./NewFooterV2.module.css";

const NewFooterV2 = props => {
  // Get current pathname
  const currentPath = props.location?.pathname || "";

  // Get constants
  const {
    FOOTER_NAV_ITEMS,
    FOOTER_SOCIAL_LINKS,
    FOOTER_APP_LINKS,
    FOOTER_CONTACT_INFO,
    FOOTER_LEGAL_LINKS,
  } = constants;

  return (
    <footer className={styles.footerWrapper}>
      <div className="container">
        <div className={styles.footerContent}>
          {/* Top Section - Desktop */}
          <div className={styles.footerTopDesktop}>
            {/* Navigation Links - Left */}
            <nav className={styles.navLinksDesktop}>
              {FOOTER_NAV_ITEMS.map((item, index) => (
                <Link
                  key={index}
                  to={item.link}
                  className={styles.navLink}
                  onClick={() => trackFooterNavClick(item.label, currentPath)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Logo Right */}
            <div className={styles.logoRight}>
              <Link to="/" onClick={() => trackFooterLogoClick(currentPath)}>
                <StaticImage
                  src="../../images/twin-logo/twin_logo_white.png"
                  alt="Twin Health India Logo"
                  placeholder="blurred"
                  quality={90}
                  className={styles.logoImage}
                />
              </Link>
            </div>
          </div>

          {/* Middle Section - Social, Apps, Contact */}
          <div className={styles.footerMiddle}>
            {/* Social and Apps Column */}
            <div className={styles.socialAppsColumn}>
              {/* Social Media Icons */}
              <div className={styles.socialIcons}>
                <a
                  href={FOOTER_SOCIAL_LINKS[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  onClick={() =>
                    trackSocialClick(FOOTER_SOCIAL_LINKS[0].name, currentPath)
                  }
                  aria-label="LinkedIn"
                >
                  <StaticImage
                    src="../../images/icons/linkedin_icon.png"
                    alt="LinkedIn Icon"
                    placeholder="none"
                    quality={100}
                    className={styles.socialIconImage}
                  />
                </a>
                <a
                  href={FOOTER_SOCIAL_LINKS[1].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  onClick={() =>
                    trackSocialClick(FOOTER_SOCIAL_LINKS[1].name, currentPath)
                  }
                  aria-label="Facebook"
                >
                  <StaticImage
                    src="../../images/icons/facebook_icon.png"
                    alt="Facebook Icon"
                    placeholder="none"
                    quality={100}
                    className={styles.socialIconImage}
                  />
                </a>
                <a
                  href={FOOTER_SOCIAL_LINKS[2].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  onClick={() =>
                    trackSocialClick(FOOTER_SOCIAL_LINKS[2].name, currentPath)
                  }
                  aria-label="Instagram"
                >
                  <StaticImage
                    src="../../images/icons/insta_icon.png"
                    alt="Instagram Icon"
                    placeholder="none"
                    quality={100}
                    className={styles.socialIconImage}
                  />
                </a>
                <a
                  href={FOOTER_SOCIAL_LINKS[3].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  onClick={() =>
                    trackSocialClick(FOOTER_SOCIAL_LINKS[3].name, currentPath)
                  }
                  aria-label="Twitter"
                >
                  <StaticImage
                    src="../../images/icons/x_icn.png"
                    alt="Twitter Icon"
                    placeholder="none"
                    quality={100}
                    className={styles.socialIconImage}
                  />
                </a>
                <a
                  href={FOOTER_SOCIAL_LINKS[4].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  onClick={() =>
                    trackSocialClick(FOOTER_SOCIAL_LINKS[4].name, currentPath)
                  }
                  aria-label="YouTube"
                >
                  <StaticImage
                    src="../../images/icons/youtube_icon.png"
                    alt="YouTube Icon"
                    placeholder="none"
                    quality={100}
                    className={styles.socialIconImage}
                  />
                </a>
              </div>

              {/* App Store Buttons */}
              <div className={styles.appButtons}>
                <a
                  href={FOOTER_APP_LINKS.appStore.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAppStoreClick("App Store", currentPath)}
                >
                  <StaticImage
                    src="../../images/icons/app_store_img.png"
                    alt="Download on App Store"
                    placeholder="none"
                    quality={100}
                    className={styles.appButton}
                  />
                </a>
                <a
                  href={FOOTER_APP_LINKS.playStore.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAppStoreClick("Google Play", currentPath)}
                >
                  <StaticImage
                    src="../../images/icons/google_play_img.png"
                    alt="Get it on Google Play"
                    placeholder="none"
                    quality={100}
                    className={styles.appButton}
                  />
                </a>
              </div>
              {/* Contact Information */}
              <div className={styles.contactInfo}>
                <a
                  href={FOOTER_CONTACT_INFO.phone.link}
                  className={styles.contactLink}
                  onClick={() => trackContactClick("Phone", currentPath)}
                >
                  <StaticImage
                    src="../../images/icons/call_icon.png"
                    alt="Phone Icon"
                    placeholder="none"
                    quality={100}
                    className={styles.contactIcon}
                  />
                  <span>{FOOTER_CONTACT_INFO.phone.display}</span>
                </a>
                <a
                  href={FOOTER_CONTACT_INFO.email.link}
                  className={styles.contactLink}
                  onClick={() => trackContactClick("Email", currentPath)}
                >
                  <StaticImage
                    src="../../images/icons/mail_icon.png"
                    alt="Email Icon"
                    placeholder="none"
                    quality={100}
                    className={styles.contactIcon}
                  />
                  <span>{FOOTER_CONTACT_INFO.email.display}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Links */}
          <nav className={styles.navLinksMobile}>
            {FOOTER_NAV_ITEMS.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className={styles.navLinkMobile}
                onClick={() => trackFooterNavClick(item.label, currentPath)}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Bottom Section - Legal Links and Copyright */}
          <div className={styles.footerBottom}>
            <div className={styles.legalLinks}>
              {FOOTER_LEGAL_LINKS.map((link, index) => (
                <React.Fragment key={index}>
                  <Link
                    to={link.link}
                    className={styles.legalLink}
                    onClick={() => trackLegalLinkClick(link.label, currentPath)}
                  >
                    {link.label}
                  </Link>
                  {index < FOOTER_LEGAL_LINKS.length - 1 && (
                    <span className={styles.separator}>|</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Copyright on Right */}
            <div className={styles.copyright}>
              <p>Twin Health India, 2025. All Rights Reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default NewFooterV2;
